import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, BookOpen, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
        setPaymentInfo(response.data);

        if (response.data.payment_status === "paid") {
          setStatus("success");
        } else if (response.data.status === "expired") {
          setStatus("expired");
        } else if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus("pending");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#1E3A5F] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#1E3A5F]">Processing Payment...</h2>
          <p className="text-gray-600 mt-2">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center bg-gray-50 py-20">
      <div className="max-w-md mx-auto px-6 text-center">
        {status === "success" && (
          <>
            <div className="h-24 w-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Successful!
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you for your purchase. Your payment has been processed successfully.
            </p>
            {paymentInfo && (
              <div className="bg-white rounded-xl p-6 shadow-sm mb-8 text-left">
                <h3 className="font-semibold text-[#1E3A5F] mb-4">Payment Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount:</span>
                    <span className="font-medium">${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status:</span>
                    <span className="font-medium text-green-600">Paid</span>
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard">
                <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white" data-testid="go-to-dashboard-btn">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Go to Dashboard
                </Button>
              </Link>
              <Link to="/school">
                <Button variant="outline" className="border-[#9D9045] text-[#9D9045]">
                  Browse More Courses
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <div className="h-24 w-24 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-8">
              <Loader2 className="h-12 w-12 text-yellow-600" />
            </div>
            <h1 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Processing
            </h1>
            <p className="text-gray-600 mb-8">
              Your payment is being processed. This may take a few moments. Please check your email for confirmation.
            </p>
            <Link to="/dashboard">
              <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white">
                Go to Dashboard
              </Button>
            </Link>
          </>
        )}

        {(status === "error" || status === "expired") && (
          <>
            <div className="h-24 w-24 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-8">
              <span className="text-4xl">⚠️</span>
            </div>
            <h1 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="text-gray-600 mb-8">
              {status === "expired" 
                ? "Your payment session has expired. Please try again."
                : "We couldn't verify your payment. Please contact support if you were charged."
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/school">
                <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white">
                  Try Again
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]">
                  Contact Support
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
